import React, { useContext } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Register from './components/register';
import Login from './components/login';
import { AuthProvider, AuthContext } from './context/AuthContext';
import Dashboard from './layout/dashboard';
import Chat from './layout/chat';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { ChatProvider } from './context/ChatContext';
import Report from './layout/report';

/**
 * PrivateRoute component that restricts access to authenticated users.
 * Redirects to login if the user is not authenticated.
 * 
 * @param {Object} props - The props object.
 * @param {JSX.Element} props.element - The component to render.
 * @returns {JSX.Element} - The rendered component or redirect.
 */
const PrivateRoute = ({ element }) => {
    const { auth } = useContext(AuthContext);
    return auth ? element : <Navigate to="/login" />;
};

/**
 * PublicRoute component that restricts access to unauthenticated users.
 * Redirects to dashboard if the user is authenticated.
 * 
 * @param {Object} props - The props object.
 * @param {JSX.Element} props.element - The component to render.
 * @returns {JSX.Element} - The rendered component or redirect.
 */
const PublicRoute = ({ element }) => {
    const { auth } = useContext(AuthContext);
    return !auth ? element : <Navigate to="/dashboard" />;
};

/**
 * App component that sets up the routing structure of the application.
 * 
 * @returns {JSX.Element} - The root component.
 */
function App() {
    return (
        <AuthProvider>
            <ChatProvider>
                <Router>
                    <Routes>
                        <Route path="/register" element={<PublicRoute element={<Register />} />} />
                        <Route path="/login" element={<PublicRoute element={<Login />} />} />
                        <Route path="/dashboard" element={<PublicRoute element={<Dashboard />} />} />
                        <Route path="/chat" element={<PublicRoute element={<Chat />} />} />
                        <Route path="/report" element={<PublicRoute element={<Report />} />} />
                        <Route path="*" element={<Navigate to="/dashboard" />} />
                    </Routes>
                </Router>
            </ChatProvider>
        </AuthProvider>
    );
}

export default App;
